import { Icon } from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import autobind from "autobind-decorator";
import * as React from "react";
import styled from 'styled-components';

import NonIdealState from "common/fragments/NonIdealState";
import Padding from "common/fragments/Padding";
import { BlockVariant, BlockView } from "common/views/block/BlockView.view";
import { ButtonVariant, ButtonView } from "common/views/button/Button.view";
import { DropdownSelectVariant } from "common/views/dropdownSelect/DropdownSelect.view";
import { OptionDropdownSelect } from "common/views/dropdownSelect/OptionDropdownSelect";
import HeadingView from "common/views/heading/Heading.view";
import Query from 'core/api/QueryComponent';
import Label from "core/components/label/Label";
import { IPanelProps, Panel } from "core/decorators/Panel";
import { isEmpty } from 'core/util/ArrayUtils';
import { validateList } from "core/util/ParamUtil";
import StringUtil from "core/util/StringUtil";
import { iDef } from "core/util/TypeUtils";
import FollowersPanel from "./component/subscriptions/FollowersPanel";
import TopicSubscriptionManage from "./component/subscriptions/TopicSubscriptionManage";
import UserSubscriptionManage from "./component/subscriptions/UserSubscriptionManage";
import TopicComponent from "./component/topic/TopicComponent";
import TopicDisplayMode from "./component/topic/TopicDisplayMode";
import TopicFormComponent from "./component/topic/TopicFormComponent";
import TopicListComponent from "./component/topic/TopicListComponent";
import TopicsPanelConfig from "./config/TopicsPanelConfig";
import Topic from "./entity/Topic";
import labelProvider from './Forms.labels';
import Tag from "common/form/entities/Tag";
import EntityMultiTagInput from "common/views/tag/EntityMultiTagInput.view";
import { ID } from "common/entities/IEntity";
import FeaturedTopics from "./FeaturedTopics";
import ForumSubscriptionManage from "./component/subscriptions/ForumSubscriptionManage";
import showTagSetOnFirstTag from "./ForumTagUtils";
import { setPageHeading } from "common/fragments/PageHeading";
import TokenSession from "core/util/session/TokenSession";
import { TextInputVariant, TextInputView } from "common/views/textInput/TextInput.view";
import { CheckBoxView } from "common/views/checkbox/Checkbox.view";
import PermissionQuery from "./entity/ForumPermissions";
import Loading from "common/fragments/Loading";
import { ForumSubscription } from "./entity/ForumSubscription";
import ServiceRegistry from "core/util/ServiceRegistry";
import IConfigService from "core/service/config/IConfigService";
import ForumSubscriptionConfig from "./config/ForumSubscriptionConfig";

const { Labels } = labelProvider;

const Header = styled.div`
	display: flex;
	
	padding-bottom: 15px;
`;
const SearchBox = styled.input`
	border: 0;
	font-size: 15px;
	border-bottom: 1px solid #ccc;
	flex: 1 0 25%;
	margin:0px 30px;
`;

const NewPostDiv = styled.div`
	flex: 1 0 5%; 
	margin:0px  20px;
`;
const ManageSubsDiv = styled.div`
	flex: 1 0 10%; 
	margin:0px  20px;
`;
const TagsDiv = styled.div`
	flex: 1 0 15%; 
	margin:0px  30px;	
`;
const sortOptions = [
	{ value: '-createdOn', label: "Newest" },
	{ value: 'createdOn', label: "Oldest" },
	{ value: 'name', label: "Name" },
	{ value: 'category.name', label: "Category name" }
];

export const PARAM_FORUM_PAGE_SIZE = "forum.pageSize";
export const PARAM_FORUM_SORT = "forum.sort";
export const PARAM_FORUM_QUERY = "forum.query";
export const PARAM_FORUM_CATEGORY = "categoryId";

interface ITopicsPanelState {
	transientTopics: Topic[];
	sort: string;
	pageSize: number;
	searchValue: string;
	inputSearchValue: string;
	view: string;
	active: {
		topic: Topic;
		currentDepth: number;
		reply: boolean;
	};
	selectedTagIds: ID[];
	excludedTopicIds: number[];
	showBookmarkTopics: boolean;
	writePermision?: boolean;
}

@Panel({ id: "forumFeed", configClass: TopicsPanelConfig, pageParams: true, labelProvider, sharedState: { forumId: null } })
class TopicsPanel extends React.Component<IPanelProps<TopicsPanelConfig>, ITopicsPanelState> {
	constructor(props) {
		super(props);

		this.state = {
			transientTopics: [],
			sort: "",
			pageSize: 12,
			searchValue: "",
			view: "overview",
			active: null,
			selectedTagIds: [],
			excludedTopicIds: [],
			inputSearchValue: "",
			showBookmarkTopics: false,
			writePermision: null,
		};

	}

	public componentDidMount() {
		this.props.actions.removePageParam("topic");
	}

	// if the category chagnes to back to the overview
	public componentDidUpdate(prevProps, prevState) {
		if(prevProps.pageParams[PARAM_FORUM_CATEGORY] !== this.props.pageParams[PARAM_FORUM_CATEGORY]) {
			this.setState({view: "overview"});
		}
	}

	private onChangeParam = (name: string) => (value: any) => {
		if (iDef(value)) {
			this.props.actions.pushPageParam(name, value);
			if (name === PARAM_FORUM_SORT) {
				this.setState({
					sort: value
				});
			} else if (name === PARAM_FORUM_PAGE_SIZE) {
				this.setState({
					pageSize: parseInt(value)
				});
			}
		} else {
			this.props.actions.removePageParam(name);
		}
	}
	@autobind
	public onChangeSearchTerm(val: string) {
		// const searchTerm = event.target.value;
		// this.setState({ searchValue: searchTerm });
		this.setState({ inputSearchValue: val })
	}


	@autobind
	public clear() {
		this.setState({ inputSearchValue: "" });
		this.setState({searchValue: ""});
	}

	@autobind
	public onSubmit (e?: any) {
		if (e) {
			e.preventDefault();
		}
		if(this.state.inputSearchValue) {
			this.setState({ searchValue: this.state.inputSearchValue })
		}
		
	}

	private renderOverview = (params, writePermisionSaved) => {
		const { config, pageParams } = this.props;

		const defaultPageSize: number = iDef(config.pageSize) ? config.pageSize : 12;
		const p = Object.entries(config.topicParams || {}).reduce((a, [k, v]) => {
			a[k] = v;
			return a;
		}, {});

		if (!pageParams[PARAM_FORUM_SORT]) {
			pageParams[PARAM_FORUM_SORT] = p['sort'];
		}
		const queryParams = {};
		queryParams['userId'] = TokenSession.getUserId();
		queryParams['entityId'] = this.props.forumId;
		queryParams['entityType'] = '13';

		return (
			<React.Fragment>
				{setPageHeading("Community")}
				{config.enablePermissionsFilter ?
				<Query.One entity={PermissionQuery} args={queryParams}>
					{(args) => {
						if (args.loading) {
							return <Loading />;
						}

						if (!args.data) {
							return (
								<NonIdealState
									title="Invalid Forum"
									description=""
								/>
							);
						}
						const response = args.data;
							if (typeof writePermisionSaved == "undefined") {
								this.props.actions.saveUserPermission(response.action === "Write"); // saving the permission in store
							}
						return (
							<React.Fragment>
				<Header>
									{(TokenSession.hasRoles("ROLE_ADMIN")) ?
										<NewPostDiv>
											<ButtonView viewKey="topic.new" onClick={() => this.setState({ view: "new" })}>
												<Labels.NewPost />
											</ButtonView>
										</NewPostDiv> : (config.enablePermissionsFilter && response && response.action === "Write") ?
											<NewPostDiv>
						<ButtonView viewKey="topic.new" onClick={() => this.setState({view: "new"})}>
							<Labels.NewPost />
						</ButtonView>
											</NewPostDiv> : <React.Fragment></React.Fragment>

					}

					<ManageSubsDiv>
						<ButtonView viewKey="subs.manage" onClick={() => this.setState({view: "manage"})}>
							<Icon icon={IconNames.COG} />&nbsp;
							<Labels.ManageSubscriptions />
						</ButtonView>
						</ManageSubsDiv>
					
					{config.enableSearchBar && (
						<React.Fragment>
							<div id="searchBar">
								<TextInputView
									viewKey="searchbar"
									variant={TextInputVariant.SEARCH}
									value={this.state.inputSearchValue}
									onChange={this.onChangeSearchTerm}
									style={{flexGrow: 1}}
									ariaLabel="searchBar"
									/>
									{ this.state.searchValue ? (
										<ButtonView onClick={this.clear} viewKey="clear">
											<Icon icon="cross" />
										</ButtonView>
									) : (
										<ButtonView onClick={()=>this.onSubmit()}  viewKey="go">
											<Icon icon="search" />
										</ButtonView>
									)}
							</div>

						</React.Fragment>
					)}
					{config.enableTagFilter && (
						<TagsDiv>
							<EntityMultiTagInput
								viewKey="refine.tag"
								onChange={(ids) => this.setState({selectedTagIds:ids})}
								value={this.state.selectedTagIds}
								entity={Tag}
								getKey={(c) => c.id}
								getSearchParams={(query) => {
									//query = query || "";
									return {
										query,
										entityTypeNames:["com.digitalignite.module.forum.entity.Topic"],
										sortByTagSetName:true
									};
								}}
								placeholder="Filter With Tags"
								onBeforeRenderItem={showTagSetOnFirstTag}
								openOnKeyDown={false}
								queryOnStartup={""}
								ariaLabel="Filter with Tags"
							/>
						</TagsDiv>
					)}
					{config.enableRefine && (
						<BlockView key="refine" variant={BlockVariant.REFINE} viewKey="refine">
							<OptionDropdownSelect
								onChange={this.onChangeParam(PARAM_FORUM_SORT)}
								options={sortOptions}
								value={this.state.sort || pageParams[PARAM_FORUM_SORT]}
								variant={DropdownSelectVariant.MINIMAL}
								viewKey="refine.sort"
							>
								<Labels.Sort />
							</OptionDropdownSelect>
						</BlockView>
					)}
				</Header>
				<CheckBoxView
						viewKey={'bookmarked.checkbox'}
						value={this.state.showBookmarkTopics}
						onChange={() => this.setState({ showBookmarkTopics: !this.state.showBookmarkTopics })}
					label={<Label k="topics.show.bookMark" v="Only bookmarked" />}
				/>

				{config.enableFeaturedTopics && (
					<FeaturedTopics 
						pushParamAction={(name, topic) => this.props.actions.pushPageParam(name, topic)}
						params={params}
						topicConfig={{...config.topicConfig, topicLayout: config.topicLayout}}
						listConfig={config.listConfig}
						onTopicNavigate={(topic, currentDepth, reply) => this.onTopicClick(topic, currentDepth, reply)}
						tagIdsParam={this.state.selectedTagIds}
						searchValue={this.state.searchValue}
						tagsEnabled={this.props.config.enableTagFilter}
						setExcludedTopicIds={(topicIds: number[])=>this.setState({excludedTopicIds:topicIds})}
						excludedTopicIds={this.state.excludedTopicIds}
						showBookmarkTopics={this.state.showBookmarkTopics}
										writePermision={response.action === "Write"}
										permissionFilterEnabled={config.enablePermissionsFilter}
					/>
				)}
				<TopicListComponent
					pushParamAction={(name, topic) => this.props.actions.pushPageParam(name, topic)}
					mode={TopicDisplayMode.OVERVIEW}
					sortParam={this.state.sort || pageParams[PARAM_FORUM_SORT]}
					pageSizeParam={this.state.pageSize || pageParams[PARAM_FORUM_PAGE_SIZE]}
					params={params}
					topicConfig={{...config.topicConfig, topicLayout: config.topicLayout}}
					listConfig={config.listConfig}
					onTopicNavigate={(topic, currentDepth, reply) => this.onTopicClick(topic, currentDepth, reply)}
					transientTopics={this.state.transientTopics}
					currentDepth={0}
					tagIdsParam={this.state.selectedTagIds}
					searchValue={this.state.searchValue}
					tagsEnabled={this.props.config.enableTagFilter}
					excludedTopicIds={this.state.excludedTopicIds}
					showBookmarkTopics={this.state.showBookmarkTopics}
									writePermision={response.action === "Write"}
									permissionFilterEnabled={config.enablePermissionsFilter}
				/>

				<Query.Many entity={Topic} args={{
					...params,
					sort: this.state.sort || pageParams[PARAM_FORUM_SORT] ? this.state.sort || pageParams[PARAM_FORUM_SORT] : null,
					pageSize: this.state.pageSize || pageParams[PARAM_FORUM_PAGE_SIZE] ? this.state.pageSize || pageParams[PARAM_FORUM_PAGE_SIZE] : null,
					tagIds: this.state.selectedTagIds && this.state.selectedTagIds.length ? this.state.selectedTagIds : null,
					query: this.state.searchValue? this.state.searchValue : null
				}}>
					{
						({ data, loading }) => {

							if (isEmpty(data)) {
								return (
									<NonIdealState
										key="empty"
										title={<Label k="forum.empty" v="" />}
										description={<Label k="forum.empty.desc" v="" />}
									/>
								);
							}
							return config.enableLoadMore && data.length >= this.state.pageSize && (
								<BlockView variant={BlockVariant.PAGING} viewKey="paging">
									<ButtonView onClick={() => this.onChangeParam(PARAM_FORUM_PAGE_SIZE)(this.state.pageSize + defaultPageSize)}
												variant={ButtonVariant.PRIMARY}
												viewKey="load-more"
									>
										<Labels.LoadMore />
									</ButtonView>
								</BlockView>
							);
						}
					}
				</Query.Many>
							</React.Fragment>
						)
					}}
					</Query.One> : (
						<React.Fragment>
							<Header>
								{(TokenSession.hasRoles("ROLE_ADMIN")) ?
									<NewPostDiv>
										<ButtonView viewKey="topic.new" onClick={() => this.setState({ view: "new" })}>
											<Labels.NewPost />
										</ButtonView>
									</NewPostDiv> : <React.Fragment></React.Fragment>
								}

								<ManageSubsDiv>
									<ButtonView viewKey="subs.manage" onClick={() => this.setState({ view: "manage" })}>
										<Icon icon={IconNames.COG} />&nbsp;
										<Labels.ManageSubscriptions />
									</ButtonView>
								</ManageSubsDiv>

								{config.enableSearchBar && (
									<React.Fragment>
										<div id="searchBar">
											<TextInputView
												viewKey="searchbar"
												variant={TextInputVariant.SEARCH}
												value={this.state.inputSearchValue}
												onChange={this.onChangeSearchTerm}
												style={{ flexGrow: 1 }}
												ariaLabel="searchBar"
											/>
											{this.state.searchValue ? (
												<ButtonView onClick={this.clear} viewKey="clear">
													<Icon icon="cross" />
												</ButtonView>
											) : (
												<ButtonView onClick={() => this.onSubmit()} viewKey="go">
													<Icon icon="search" />
												</ButtonView>
											)}
										</div>

									</React.Fragment>
								)}
								{config.enableTagFilter && (
									<TagsDiv>
										<EntityMultiTagInput
											viewKey="refine.tag"
											onChange={(ids) => this.setState({ selectedTagIds: ids })}
											value={this.state.selectedTagIds}
											entity={Tag}
											getKey={(c) => c.id}
											getSearchParams={(query) => {
												//query = query || "";
												return {
													query,
													entityTypeNames: ["com.digitalignite.module.forum.entity.Topic"],
													sortByTagSetName: true
												};
											}}
											placeholder="Filter With Tags"
											onBeforeRenderItem={showTagSetOnFirstTag}
											openOnKeyDown={false}
											queryOnStartup={""}
											ariaLabel="Filter with Tags"
										/>
									</TagsDiv>
								)}
								{config.enableRefine && (
									<BlockView key="refine" variant={BlockVariant.REFINE} viewKey="refine">
										<OptionDropdownSelect
											onChange={this.onChangeParam(PARAM_FORUM_SORT)}
											options={sortOptions}
											value={this.state.sort || pageParams[PARAM_FORUM_SORT]}
											variant={DropdownSelectVariant.MINIMAL}
											viewKey="refine.sort"
										>
											<Labels.Sort />
										</OptionDropdownSelect>
									</BlockView>
								)}
							</Header>
							<CheckBoxView
								viewKey={'bookmarked.checkbox'}
								value={this.state.showBookmarkTopics}
								onChange={() => this.setState({ showBookmarkTopics: !this.state.showBookmarkTopics })}
								label={<Label k="topics.show.bookMark" v="Only bookmarked" />}
							/>

							{config.enableFeaturedTopics && (
								<FeaturedTopics
									pushParamAction={(name, topic) => this.props.actions.pushPageParam(name, topic)}
									params={params}
									topicConfig={{ ...config.topicConfig, topicLayout: config.topicLayout }}
									listConfig={config.listConfig}
									onTopicNavigate={(topic, currentDepth, reply) => this.onTopicClick(topic, currentDepth, reply)}
									tagIdsParam={this.state.selectedTagIds}
									searchValue={this.state.searchValue}
									tagsEnabled={this.props.config.enableTagFilter}
									setExcludedTopicIds={(topicIds: number[]) => this.setState({ excludedTopicIds: topicIds })}
									excludedTopicIds={this.state.excludedTopicIds}
									showBookmarkTopics={this.state.showBookmarkTopics}
								// writePermision={response.action === "Write"}
								/>
							)}
							<TopicListComponent
								pushParamAction={(name, topic) => this.props.actions.pushPageParam(name, topic)}
								mode={TopicDisplayMode.OVERVIEW}
								sortParam={this.state.sort || pageParams[PARAM_FORUM_SORT]}
								pageSizeParam={this.state.pageSize || pageParams[PARAM_FORUM_PAGE_SIZE]}
								params={params}
								topicConfig={{ ...config.topicConfig, topicLayout: config.topicLayout }}
								listConfig={config.listConfig}
								onTopicNavigate={(topic, currentDepth, reply) => this.onTopicClick(topic, currentDepth, reply)}
								transientTopics={this.state.transientTopics}
								currentDepth={0}
								tagIdsParam={this.state.selectedTagIds}
								searchValue={this.state.searchValue}
								tagsEnabled={this.props.config.enableTagFilter}
								excludedTopicIds={this.state.excludedTopicIds}
								showBookmarkTopics={this.state.showBookmarkTopics}
							// writePermision={response.action === "Write"}
							/>

							<Query.Many entity={Topic} args={{
								...params,
								sort: this.state.sort || pageParams[PARAM_FORUM_SORT] ? this.state.sort || pageParams[PARAM_FORUM_SORT] : null,
								pageSize: this.state.pageSize || pageParams[PARAM_FORUM_PAGE_SIZE] ? this.state.pageSize || pageParams[PARAM_FORUM_PAGE_SIZE] : null,
								tagIds: this.state.selectedTagIds && this.state.selectedTagIds.length ? this.state.selectedTagIds : null,
								query: this.state.searchValue ? this.state.searchValue : null
							}}>
								{
									({ data, loading }) => {

										if (isEmpty(data)) {
											return (
												<NonIdealState
													key="empty"
													title={<Label k="forum.empty" v="" />}
													description={<Label k="forum.empty.desc" v="" />}
												/>
											);
										}
										return config.enableLoadMore && data.length >= this.state.pageSize && (
											<BlockView variant={BlockVariant.PAGING} viewKey="paging">
												<ButtonView onClick={() => this.onChangeParam(PARAM_FORUM_PAGE_SIZE)(this.state.pageSize + defaultPageSize)}
													variant={ButtonVariant.PRIMARY}
													viewKey="load-more"
												>
													<Labels.LoadMore />
												</ButtonView>
											</BlockView>
										);
									}
								}
							</Query.Many>
						</React.Fragment>
					)}
			</React.Fragment>
		);
	}

	private renderNewPostPanel = () => (
		<React.Fragment>
			<TopicFormComponent categoryId={this.props.pageParams[PARAM_FORUM_CATEGORY] || this.props.config.categoryId} onAfterSave={(topic) => {
				this.setState({ transientTopics: [topic, ...this.state.transientTopics], view: "overview" });
			}} border={true} tagsEnabled={this.props.config.enableTagFilter} config={{...this.props.config.topicConfig}}/>
		</React.Fragment>
	)

	private renderMangagePanel = () => (

		<React.Fragment>
			<HeadingView viewKey="topic.subs.title">
				<Labels.FollowedForums />
			</HeadingView>

			<ForumSubscriptionManage />

			<Padding.Base />

			<HeadingView viewKey="topic.subs.title">
				<Labels.FollowedUsers />
			</HeadingView>

			<UserSubscriptionManage />

			<Padding.Base />

			<HeadingView viewKey="topic.subs.title">
				<Labels.Followers />
			</HeadingView>

			<FollowersPanel />

			<Padding.Base />

			<HeadingView viewKey="user.subs.title">
				<Labels.SubscribedDiscussions />
			</HeadingView>

			<TopicSubscriptionManage  />
			<Padding.Base />

			<HeadingView viewKey="favourite.subs.title">
				<Labels.FavouriteTopics />
			</HeadingView>

			{/*<FavouriteSubscriptionManage onTopicClick={this.onTopicClick} />*/}
		</React.Fragment>
	)

	private renderTopicPanel = () => {
		const {topic, currentDepth, reply } = this.state.active;
		const {topicConfig, listConfig, topicLayout} = this.props.config;

		return (
			<React.Fragment>
				<TopicComponent
					mode={TopicDisplayMode.PARENT}
					topic={topic}
					config={{...topicConfig, topicLayout }}
					listConfig={listConfig}
					onTopicNavigate={(topic, currentDepth, reply) => this.onTopicClick(topic, currentDepth, reply)}
					currentDepth={currentDepth}
					reply={reply}
					tagsEnabled={this.props.config.enableTagFilter}
					writePermision={this.props.userPermission}
					permissionFilterEnabled={this.props.config.enablePermissionsFilter || typeof this.props.userPermission !== "undefined"}
				/>
			</React.Fragment>
		);
	}

	@autobind
	private onTopicClick(topic: Topic, currentDepth, reply) {
		this.setState({view: "topic", active: {topic, currentDepth, reply}});
	}

	
	/**
	 * This function handles searching courses based on search term
	 * @param name
	 */
	public searchTopics = (name) => {
		const searchTerm = this.state.searchValue;
		if (iDef(searchTerm)) {
			this.props.actions.pushPageParam(name, searchTerm);
		}
	}

	public render() {
		const { config, pageParams } = this.props;

		const defaultPageSize: number = iDef(config.pageSize) ? config.pageSize : 12;
		const currentPageSize: number = StringUtil.toInteger(pageParams[PARAM_FORUM_PAGE_SIZE], defaultPageSize);

		const params = {
			pageSize: currentPageSize,
			sort: validateList(['-createdOn', 'createdOn', 'name', 'category.name'], this.state.sort || pageParams[PARAM_FORUM_SORT], null),
			category: pageParams[PARAM_FORUM_CATEGORY] || this.props.config.categoryId
		};

		const back = (
			<ButtonView
				viewKey="forum.back"
				onClick={() => this.setState({view: "overview"})}
				icon={IconNames.CHEVRON_LEFT}
			>
				<Label k="forum.back" v="Overview" />
			</ButtonView>
		);
		const writePermisionSaved = this.props.userPermission; // if there is a saved permission in the store
		switch(this.state.view) {

			case "overview":
				return this.renderOverview(params, writePermisionSaved);
			case "topic":
				return (
					<React.Fragment>
						{back}
						{this.renderTopicPanel()}
					</React.Fragment>
				);
			case "new":
				return (
					<React.Fragment>
						{back}
						{this.renderNewPostPanel()}
					</React.Fragment>
				);
			case "manage":
				return (
					<React.Fragment>
						{back}
						{this.renderMangagePanel()}
					</React.Fragment>
				);
			default:
				return null;
		}
	}
}



// WEBPACK FOOTER //
// ./src/panels/forum/Forms.panel.tsx